import { PHASE } from "../../../common/constants.ts";
import { g } from "../../util/index.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import type {
	PlayerContract,
	PlayerWithoutKey,
} from "../../../common/types.ts";

/**
 * Store a contract in a player object.
 *
 * @memberOf core.player
 * @param {Object} p Player object.
 * @param {Object} contract Contract object with two properties,    exp (year) and amount (thousands of dollars).
 * @param {boolean} signed Is this an official signed contract (true), or just part of a negotiation (false)?
 * @return {Object} Updated player object.
 */
// The Gui's rule: a player under 21 signs at least through the season in which he turns 21.
// Age follows the game's convention, season - born.year, so that season is born.year + 21.
export const MIN_CONTRACT_AGE = 21;
export const minContractExp = (p: { born?: { year: number } }) =>
	isSport("baseball") &&
	p.born !== undefined &&
	g.get("season") - p.born.year < MIN_CONTRACT_AGE
		? p.born.year + MIN_CONTRACT_AGE
		: -Infinity;

const setContract = (
	p: Pick<PlayerWithoutKey, "contract" | "salaries"> & {
		born?: { year: number };
	},
	contract: PlayerContract,
	signed: boolean,
) => {
	// Sigh, don't know why this is needed, but people tell me that sometimes the #1 pick has a massively negative contract
	if (contract.amount < 0) {
		contract.amount = g.get("minContract");
	}

	// Applies to every signing path (draft, free agency, re-signing, CPU and user, main roster or minors)
	if (signed && contract.exp < minContractExp(p)) {
		contract = { ...contract, exp: minContractExp(p) };
	}

	p.contract = contract;

	// Only write to salary log if the player is actually signed. Otherwise, we're just generating a value for a negotiation.
	if (signed) {
		// Is this contract beginning with an in-progress season, or next season?
		let start = g.get("season");

		if (g.get("phase") > PHASE.AFTER_TRADE_DEADLINE) {
			start += 1;
		}

		for (let i = start; i <= p.contract.exp; i++) {
			p.salaries.push({
				season: i,
				amount: contract.amount,
			});
		}
	}
};

export default setContract;
